/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import { createSlice } from '@reduxjs/toolkit';
import { STORAGE_KEYS } from '../../constants/main';
import { CONVERSATION_STATUSES, LIVECHAT_SEND_MESSAGE_MODE } from '../../constants/liveChat';

const initialState = {
  isEstablishingConnection: undefined,
  connectionError: null,
  isConnected: false,

  isBeingAuthorized: undefined,
  authorizationError: null,
  isAuthorized: false,

  isExportingTranscript: false,
  exportTranscriptError: null,

  activeConversationInfo: null,
  activeConversationInvalidated: null,
  isFetchingActiveConversation: false,
  fetchActiveConversationError: null,

  conversationMessages: null,
  isFetchingConversationMessages: false,
  fetchConversationMessagesError: null,

  isFetchingChatSession: false,
  fetchChatSessionError: null,

  isSendingMessage: false,
  sendMessageError: null,
  isReceivingMessage: false,
  receiveMessageError: null,

  isUploadingFiles: false,
  uploadFilesError: null,

  isTransferringConversation: false,
  transferConversationError: null,

  conversations: null,
  isFetchingConversations: false,
  fetchConversationsError: null,

  incomingRequests: null,
  isFetchingRequests: false,
  fetchRequestsError: null,

  // Livechat application settings
  livechatSettings: null,
  isFetchingLivechatSettings: false,
  fetchLivechatSettingsError: null,
  isUpdatingLivechatSettings: false,
  updateLivechatSettingsError: null,

  // This is a unique value that triggers a refetch of the live-chat related
  // resources after a reconnect event occurs (e.g after long system sleep)
  dataInvalidationTimestamp: null,

  // Livechat inbox filters applied
  inboxFilters: {
    searchText: undefined,
    status: `${CONVERSATION_STATUSES.STARTED},${CONVERSATION_STATUSES.FINISHED}`,
    unreadOnly: false,
    orderByUnread: false,
    transferredOnly: false,
    bots: [],
    users: [],
    date: '',
    customDate: [],
    skip: 0,
    replace: false
  },
  inboxFilterCount: 0,

  // Local storage values
  pushNotificationsPermission: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_PUSH_NOTIFICATIONS),
  conversationsSoundEnabled: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_CONVERSATIONS_SOUND) === 'true',
  requestsSoundEnabled: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_REQUESTS_SOUND) === 'true',
  favoriteAutomations: JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_FAVORITE_AUTOMATIONS)) || [],
  sendMessageMode: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_SEND_MESSAGE_MODE) ?? LIVECHAT_SEND_MESSAGE_MODE.ENTER,
  spellCheckLanguage: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_SPELL_CHECK_LANGUAGE) ?? null
};

const getFilterCount = (inboxFilters) => {
  let totalFilters = 0;
  const isTransferred = inboxFilters.transferredOnly;
  const isUnread = inboxFilters.unreadOnly;

  if (isUnread || isTransferred) {
    totalFilters += 1;
  }
  const bots = inboxFilters.bots?.join(',');
  const users = inboxFilters.users?.join(',');
  const date = inboxFilters.date;

  if (bots) {
    totalFilters += 1;
  }

  if (users) {
    totalFilters += 1;
  }

  if (date) {
    totalFilters += 1;
  }

  return totalFilters;
};

const livechatSlice = createSlice({
  name: 'livechat',
  initialState,
  reducers: {
    establishConnectionStart: state => ({
      ...state,
      isEstablishingConnection: true,
      connectionError: null,
      isConnected: false
    }),

    establishConnectionFail: (state, action) => ({
      ...state,
      isConnected: false,
      isEstablishingConnection: false,
      connectionError: action.payload.error
    }),

    establishConnectionComplete: state => ({
      ...state,
      isConnected: true,
      isEstablishingConnection: false
    }),

    authorizeConnectionStart: state => ({
      ...state,
      isBeingAuthorized: true,
      authorizationError: null,
      isAuthorized: false
    }),

    authorizeConnectionFail: (state, action) => ({
      ...state,
      isAuthorized: false,
      isBeingAuthorized: false,
      authorizationError: action.payload.error
    }),

    authorizeConnectionComplete: state => ({
      ...state,
      isAuthorized: true,
      isBeingAuthorized: false
    }),

    fetchConversationsStart: state => ({
      ...state,
      isFetchingConversations: true,
      fetchConversationsError: null
    }),

    fetchConversationsFail: (state, action) => ({
      ...state,
      isFetchingConversations: false,
      fetchConversationsError: action.payload.error
    }),

    fetchConversationsComplete: (state, action) => {
      const oldData = state.conversations ? state.conversations.data : [];
      return {
        ...state,
        isFetchingConversations: false,
        fetchConversationsError: null,
        conversations: {
          total: action.payload.total,
          data: [...oldData, ...action.payload.data]
        }
      };
    },

    fetchRequestsStart: state => ({
      ...state,
      isFetchingRequests: true,
      fetchRequestsError: null
    }),

    fetchRequestsFail: (state, action) => ({
      ...state,
      isFetchingRequests: false,
      fetchRequestsError: action.payload.error
    }),

    fetchRequestsComplete: (state, action) => {
      const oldData = state.incomingRequests ? state.incomingRequests.data : [];
      return {
        ...state,
        isFetchingRequests: false,
        fetchRequestsError: null,
        incomingRequests: {
          total: action.payload.total,
          data: [...oldData, ...action.payload.data]
        }
      };
    },

    acceptRequestFail: (state, action) => ({
      ...state,
      acceptRequestError: action.payload.error
    }),

    fetchConversationMessagesStart: state => ({
      ...state,
      isFetchingConversationMessages: true,
      fetchConversationMessagesError: null
    }),

    fetchConversationMessagesFail: (state, action) => ({
      ...state,
      isFetchingConversationMessages: false,
      fetchConversationMessagesError: action.payload
    }),

    fetchConversationMessagesComplete: (state, action) => {
      const oldData = state.conversationMessages ? state.conversationMessages.data : [];
      return {
        ...state,
        isFetchingConversationMessages: false,
        fetchConversationMessagesError: null,
        conversationMessages: {
          total: action.payload.total,
          data: [...oldData, ...action.payload.data]
        }
      };
    },

    fetchChatSessionStart: state => ({
      ...state,
      isFetchingChatSession: true,
      fetchChatSessionError: null
    }),

    fetchChatSessionFail: (state, action) => ({
      ...state,
      isFetchingChatSession: false,
      fetchChatSessionError: action.payload.error
    }),

    fetchChatSessionComplete: (state, action) => ({
      ...state,
      isFetchingChatSession: false,
      fetchChatSessionError: null
    }),

    exportTranscriptStart: state => ({
      ...state,
      isExportingTranscript: true,
      exportTranscriptError: null
    }),

    exportTranscriptFail: (state, action) => ({
      ...state,
      isExportingTranscript: false,
      exportTranscriptError: action.payload
    }),

    exportTranscriptComplete: (state, action) => ({
      ...state,
      isExportingTranscript: false,
      exportTranscriptError: null
    }),

    fetchActiveConversationStart: state => ({
      ...state,
      isFetchingActiveConversation: true,
      fetchActiveConversationError: null
    }),

    fetchActiveConversationFail: (state, action) => ({
      ...state,
      isFetchingActiveConversation: false,
      fetchActiveConversationError: action.payload
    }),

    fetchActiveConversationComplete: (state, action) => ({
      ...state,
      isFetchingActiveConversation: false,
      fetchActiveConversationError: null,
      activeConversationInfo: action.payload
    }),

    sendMessageStart: state => ({
      ...state,
      isSendingMessage: true,
      sendMessageError: null
    }),

    sendMessageFail: (state, action) => ({
      ...state,
      isSendingMessage: false,
      sendMessageError: action.payload.error
    }),

    sendMessageComplete: state => ({
      ...state,
      isSendingMessage: false,
      sendMessageError: null
    }),

    setSendMessageMode: (state, action) => {
      window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_SEND_MESSAGE_MODE, action.payload);
      return {
        ...state,
        sendMessageMode: action.payload
      };
    },

    setSpellCheckLanguage: (state, action) => {
      if (!action.payload || action.payload === 'null') {
        window.localStorage.removeItem(STORAGE_KEYS.LIVECHAT_SPELL_CHECK_LANGUAGE);
        return {
          ...state,
          spellCheckLanguage: null
        };
      } else {
        window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_SPELL_CHECK_LANGUAGE, action.payload);
        return {
          ...state,
          spellCheckLanguage: action.payload
        };
      }
    },

    uploadFilesStart: state => ({
      ...state,
      isUploadingFiles: true,
      uploadFilesError: null
    }),

    uploadFilesFail: (state, action) => ({
      ...state,
      isUploadingFiles: false,
      uploadFilesError: action.payload.error
    }),

    uploadFilesComplete: state => ({
      ...state,
      isUploadingFiles: false,
      uploadFilesError: null
    }),

    transferConversationStart: state => ({
      ...state,
      isTransferringConversation: true,
      transferConversationError: null
    }),

    transferConversationFail: (state, action) => ({
      ...state,
      isTransferringConversation: false,
      transferConversationError: action.payload.error
    }),

    transferConversationComplete: state => ({
      ...state,
      isTransferringConversation: false,
      transferConversationError: null
    }),

    addIncomingRequest: (state, action) => {
      return { ...state,
        incomingRequests: {
          total: state.incomingRequests.total + 1,
          data: [action.payload, ...state.incomingRequests.data]
        }
      };
    },

    removeRequest: (state, action) => {
      const requests = state.incomingRequests.data?.filter(request => request.id !== action.payload);
      return {
        ...state,
        incomingRequests: {
          total: state.incomingRequests.total - 1,
          data: requests
        }
      };
    },

    addConversation: (state, action) => ({
      ...state,
      conversations: {
        total: state.conversations.total + 1,
        data: [action.payload, ...state.conversations.data]
      }
    }),

    addReturningConversation: state => ({
      ...state,
      activeConversationInvalidated: Date.now()
    }),

    removeConversation: (state, action) => {
      if (!state.conversations) {
        return;
      }
      let updatedData = [...state.conversations.data];
      let updatedTotal = state.conversations.total;
      const conversationToRemoveIndex = updatedData.findIndex(conversation => conversation.id === action.payload.id);

      if (conversationToRemoveIndex >= 0) {
        updatedData.splice(conversationToRemoveIndex, 1);
        updatedTotal -= 1;
      }

      return {
        ...state,
        conversations: {
          total: updatedTotal,
          data: updatedData
        }
      };
    },

    updateConversationOnMessage: (state, action) => {
      if (!state.conversations) {
        return;
      }
      let updatedData = [...state.conversations.data];

      const conversationToUpdateIndex = updatedData.findIndex(conversation => conversation.id === action.payload.livechatConversation.id);
      if (conversationToUpdateIndex >= 0) {
        updatedData.splice(conversationToUpdateIndex, 1);
      }

      const updatedConversation = {
        ...updatedData[conversationToUpdateIndex],
        ...action.payload.livechatConversation,
        lastMessage: {
          id: action.payload.id,
          platformSessionId: action.payload.platformSessionId,
          sentDate: action.payload.sentDate,
          text: action.payload.text
        }
      };
      updatedData = [updatedConversation, ...updatedData];

      return {
        ...state,
        conversations: {
          total: state.conversations.total,
          data: updatedData
        }
      };
    },

    updateActiveConversation: (state, action) => {
      if (!state.activeConversationInfo) {
        return;
      }

      return {
        ...state,
        activeConversationInfo: action.payload
      };
    },

    setConversationRead: (state, action) => {
      if (!state.conversations) {
        return;
      }
      const updatedData = [...state.conversations.data];
      const { conversation, currentUserId } = action.payload;

      const conversationToUpdateIndex = (updatedData || []).findIndex(oldConversation => oldConversation.id === conversation.id);

      if (conversationToUpdateIndex >= 0) {
        const meIndex = (updatedData[conversationToUpdateIndex].livechatParticipants || []).findIndex(
          participant => participant.platformUserId === currentUserId
        );
        const updatedParticipants = [...updatedData[conversationToUpdateIndex].livechatParticipants];

        if (meIndex >= 0) {
          updatedParticipants[meIndex] = {
            ...updatedParticipants[meIndex],
            lastMessageRead: true
          };

          updatedData[conversationToUpdateIndex] = {
            ...updatedData[conversationToUpdateIndex],
            livechatParticipants: updatedParticipants
          };
        }
      }

      return {
        ...state,
        conversations: {
          total: state.conversations.total,
          data: updatedData
        }
      };
    },

    addConversationMessage: (state, action) => {
      if (action.payload.livechatConversation?.id === state.activeConversationInfo?.id) {

        const updatedConversationMessages = [...state.conversationMessages.data];
        let messageIndex = updatedConversationMessages.length;

        for (let i = 0; i < messageIndex; i++) {
          const message = updatedConversationMessages[i];
          if (message.id < action.payload.id) {
            messageIndex = i;
            break;
          }
        }

        updatedConversationMessages.splice(messageIndex, 0, action.payload);

        return {
          ...state,
          conversationMessages: {
            total: state.conversationMessages.total + 1,
            data: updatedConversationMessages
          }
        };
      }
    },

    updateLastMessage: (state, action) => {
      if (!state.conversations) {
        return;
      }
      const updatedData = [...state.conversations.data];
      const { id, lastMessage } = action.payload;

      const conversationToUpdateIndex = updatedData.findIndex(conversation => conversation.id === id);
      updatedData[conversationToUpdateIndex] = {
        ...updatedData[conversationToUpdateIndex],
        lastMessage: {
          id: lastMessage.id,
          platformSessionId: lastMessage.platformSessionId,
          sentDate: lastMessage.sentDate,
          text: lastMessage.text
        }
      };

      return {
        ...state,
        conversations: {
          total: state.conversations.total,
          data: updatedData
        }
      };
    },

    updateConversationStatus: (state, action) => {
      if (!state.conversations) {
        return;
      }
      const updatedData = [...state.conversations.data];

      const conversationToUpdateIndex = updatedData.findIndex(conversation => conversation.id === action.payload.id);
      if (conversationToUpdateIndex >= 0) {
        updatedData[conversationToUpdateIndex] = {
          ...updatedData[conversationToUpdateIndex],
          ...action.payload
        };
      }

      return {
        ...state,
        conversations: {
          total: state.conversations.total,
          data: updatedData
        }
      };
    },

    updateIncomingRequest: (state, action) => {
      if (!state.incomingRequests) {
        return;
      }

      const updatedData = [...state.incomingRequests.data];

      const conversationToUpdateIndex = updatedData.findIndex(request => request.id === action.payload.id);
      if (conversationToUpdateIndex >= 0) {
        updatedData[conversationToUpdateIndex] = {
          ...updatedData[conversationToUpdateIndex],
          ...action.payload
        };
      }

      return {
        ...state,
        incomingRequests: {
          total: state.incomingRequests.total,
          data: updatedData
        }
      };
    },

    replaceConversations: (state, action) => ({
      ...state,
      isFetchingConversations: false,
      fetchConversationsError: null,
      conversations: action.payload
    }),

    // Livechat application settings
    // GET
    livechatSettingsFetchStart: state => ({
      ...state,
      isFetchingLivechatSettings: true,
      fetchLivechatSettingsError: null
    }),

    livechatSettingsFetchFail: (state, action) => ({
      ...state,
      isFetchingLivechatSettings: false,
      fetchLivechatSettingsError: action.payload.error
    }),

    livechatSettingsFetchComplete: (state, action) => ({
      ...state,
      isFetchingLivechatSettings: false,
      fetchLivechatSettingsError: null,
      livechatSettings: action.payload
    }),

    // PUT
    livechatSettingsUpdateStart: state => ({
      ...state,
      isUpdatingLivechatSettings: true,
      updateLivechatSettingsError: null
    }),

    livechatSettingsUpdateFail: (state, action) => ({
      ...state,
      isUpdatingLivechatSettings: false,
      updateLivechatSettingsError: action.payload.error
    }),

    livechatSettingsUpdateComplete: state => ({
      ...state,
      isUpdatingLivechatSettings: false,
      updateLivechatSettingsError: null
    }),

    togglePushNotifications: (state, action) => {
      window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_PUSH_NOTIFICATIONS, action.payload);
      return {
        ...state,
        pushNotificationsPermission: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_PUSH_NOTIFICATIONS)
      };
    },

    toggleConversationsSound: (state, action) => {
      window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_CONVERSATIONS_SOUND, action.payload);
      return {
        ...state,
        conversationsSoundEnabled: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_CONVERSATIONS_SOUND) === 'true'
      };
    },

    toggleRequestsSound: (state, action) => {
      window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_REQUESTS_SOUND, action.payload);
      return {
        ...state,
        requestsSoundEnabled: window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_REQUESTS_SOUND) === 'true'
      };
    },

    setFavoriteAutomations: (state, action) => {
      window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_FAVORITE_AUTOMATIONS, JSON.stringify(action.payload));

      return {
        ...state,
        favoriteAutomations: action.payload
      };
    },

    setInboxFilters: (state, action) => {
      const filters = {
        ...state.inboxFilters,
        ...action.payload
      };

      const totalCount = getFilterCount(filters);

      return {
        ...state,
        inboxFilters: filters,
        inboxFilterCount: totalCount
      };
    },

    updateDataInvalidationTimestamp: (state, action) => ({
      ...state,
      dataInvalidationTimestamp: action.payload
    }),

    resetInboxFilters: state => {

      const totalCount = getFilterCount(initialState.inboxFilters);

      return {
        ...state,
        inboxFilters: initialState.inboxFilters,
        inboxFilterCount: totalCount
      };
    },

    clearConversations: state => ({
      ...state,
      conversations: null
    }),

    clearRequests: state => ({
      ...state,
      incomingRequests: null
    }),

    clearMessages: state => ({
      ...state,
      conversationMessages: null
    }),

    clearConversationInfo: state => ({
      ...state,
      activeConversationInfo: null
    }),

    disconnectLivechatSocket: state => ({
      ...state,
      isConnected: false,
      isAuthorized: false,
      isBeingAuthorized: false,
      isEstablishingConnection: undefined
    })
  }
});

export const livechatActions = livechatSlice.actions;

export default livechatSlice.reducer;
